export type StopReasonLevel = "ok" | "degraded" | "down";

type StopReasonInfo = {
  label: string;
  detail: string;
  level?: StopReasonLevel;
};

const STOP_REASONS: Record<string, StopReasonInfo> = {
  none: { label: "无", detail: "消息正常流转，没有停止原因。", level: "ok" },
  observe_mode: { label: "观察者未唤起", detail: "当前人格要求被 @ 或明确喊到才会接话。" },
  observe_command_denied: { label: "观察者命令门禁", detail: "观察者模式下只有管理员可以使用命令。" },
  empty_message: { label: "空消息", detail: "原始消息没有可处理文本。" },
  empty_after_clean: { label: "清理后为空", detail: "去掉 @、CQ 码后没有剩余内容。" },
  image_unrecognized: { label: "图片未识别", detail: "图片没有识别出可用文字或描述。" },
  image_vision_unavailable: { label: "图片能力不可用", detail: "视觉能力未配置或暂不可用。", level: "down" },
  image_collect_only: { label: "只收集图片", detail: "图片已作为表情或素材收集，不触发文本回复。" },
  voice_unrecognized: { label: "语音未识别", detail: "语音没有转成可用文本。" },
  voice_stt_unavailable: { label: "语音能力不可用", detail: "语音识别能力未配置或暂不可用。", level: "down" },
  command_dry_run: { label: "命令 Dry Run", detail: "调试模拟中不会真正执行命令。" },
  command_handled: { label: "命令已处理", detail: "命令已回复或完成处理。", level: "ok" },
  command_error: { label: "命令出错", detail: "命令执行失败，已给用户可见反馈。", level: "down" },
  pipeline_error: { label: "管道异常", detail: "消息处理 Pipeline 某个阶段抛错，已记录阶段和异常类型。", level: "down" },
  openclaw_handled: { label: "project-internal 已处理", detail: "自然语言触发已交给 project-internal。", level: "ok" },
  rate_limit: { label: "频率限制", detail: "用户触发频率保护。" },
  sleeping: { label: "作息睡眠", detail: "作息模拟判断当前不应回复。" },
  inactive: { label: "作息不活跃", detail: "作息模拟判断当前活跃度不足。" },
  taboo_ignore: { label: "禁区忽略", detail: "命中禁区规则并选择忽略。" },
  taboo_deflect: { label: "禁区岔开", detail: "命中禁区规则并选择岔开。" },
  low_interest: { label: "兴趣较低", detail: "在场感判断没有足够接话动机。" },
  presence_probability: { label: "在场概率未命中", detail: "按群友在场感概率保持沉默。" },
  active_side_conversation: { label: "旁聊中", detail: "群里像是在别人对话，避免插话。" },
  recent_reply: { label: "刚发过言", detail: "降低刷屏感。" },
  recent_reply_cooldown: { label: "刚发过言", detail: "降低刷屏感。" },
  duplicate_reply: { label: "复读拦截", detail: "生成回复与近期发言过近。" },
  novelty_duplicate: { label: "复读拦截", detail: "生成回复与近期发言过近。" },
  hard_duplicate: { label: "硬重复", detail: "检测到硬重复回复。" },
  stale_skipped: { label: "发送过期跳过", detail: "生成回复后群聊又有新消息或等待过久，旧回复已跳过。" },
  tts_record_mode: { label: "录音模式", detail: "当前消息被 TTS 录音流程消费。" },
  burst_merged_into_pending: { label: "连发合并", detail: "这条消息已并入同一用户的短时间连发窗口。" },
  disabled: { label: "已关闭", detail: "功能开关关闭。" },
  group_not_configured: { label: "未配置", detail: "当前群未在功能配置内。" },
  activity_below_threshold: { label: "活跃不足", detail: "群活跃度没达到主动发言阈值。" },
  bot_cooldown: { label: "Bot 冷却", detail: "Bot 冷却中。" },
  proactive_interval: { label: "主动间隔", detail: "距离上次主动发言太近。" },
  no_fresh_topic: { label: "无新话题", detail: "没有适合接上的新话题。" },
  probability_miss: { label: "概率未中", detail: "概率抽样未触发。" },
  eligible: { label: "可触发", detail: "当前可触发。", level: "ok" },
  triggered: { label: "已触发", detail: "已经触发。", level: "ok" },
};

export function reasonLabel(reason: unknown) {
  const key = normalizeReason(reason);
  if (!key) return "-";
  return STOP_REASONS[key]?.label || key;
}

export function reasonDetail(reason: unknown) {
  const key = normalizeReason(reason);
  if (!key) return "消息正常流转，没有停止原因。";
  return STOP_REASONS[key]?.detail || "暂未收录解释，保留原始原因。";
}

export function formatStopReason(reason: unknown) {
  const key = normalizeReason(reason);
  if (!key) return "-";
  const label = reasonLabel(key);
  return label === key ? key : `${label}(${key})`;
}

export function reasonSearchText(reason: unknown) {
  const key = normalizeReason(reason);
  if (!key) return "";
  return [key, reasonLabel(key), reasonDetail(key), formatStopReason(key)].join(" ");
}

export function stopReasonClass(reason: unknown): StopReasonLevel {
  const key = normalizeReason(reason);
  if (!key) return "ok";
  const knownLevel = STOP_REASONS[key]?.level;
  if (knownLevel) return knownLevel;
  if (key.includes("unavailable") || key.includes("error")) return "down";
  return "degraded";
}

function normalizeReason(reason: unknown) {
  return String(reason || "").trim();
}
